import type { Vec2, Vec3 } from '../../geo/types';
import type { WaterEdge, QuayKind } from '../../site/types';
import type { PaletteModel, PaletteClassName } from '../../data/types';
import type { VertexStyle } from '../mesh/meshBuilder';
import type { ChunkSink } from '../mesh/chunker';
import { SURFACE, edgeIdFor, type SurfaceKind } from '../mesh/attributes';
import { WORLD } from '../../core/config';
import { layoutToWorld, worldToLayout } from '../../core/coords';
import { banksOf } from '../streetscape/banks';
import { PATTERN } from '../../render/patterns/patternIds';
import { orientedFace } from '../fabric/solids';
import { buildKattengatDuiker, kattengatDuikerAt } from './kattengatDuiker';
import type { WaterAccess } from '../../site/waterAccess';
import { stairBankOpening, type BankOpening } from './stairBankOpening';

const QUAY_PALETTE: Readonly<Record<QuayKind, PaletteClassName>> = {
  brick: 'wallBrick',
  wood: 'shipHull',
  earth: 'dikeRoads',
};

const QUAY_SURFACE: Readonly<Record<QuayKind, SurfaceKind>> = {
  brick: SURFACE.brick,
  wood: SURFACE.wood,
  earth: SURFACE.quayEarth,
};

const EARTH_SLOPE_DISTANCE = 1.5;
const EARTH_BOTTOM_Y = -1.0;
// Two banks meeting round a corner of land reach out to where their feet meet, 1/cos(turn/2)
// slopes from the corner; past a 120° turn that is over twice the slope, and a fan closes it.
const MAX_MITER = 2;
const FRAME = { origin: [0, 0, 0] as const, yaw: 0 };

// A water edge with its ends taken so the water lies on the right of a→b: the chunks draw front
// faces only, and a quad [footA, footB, topB, topA] faces the right of a→b.
interface Shore {
  readonly heightAt: (p: Vec2) => number;
  readonly edge: WaterEdge;
  readonly index: number;
  readonly a: Vec2;
  readonly b: Vec2;
}

// Where the lower edge of a quay ends: a bank's foot lies 1.5 m out at −1.0, a wall's under the
// water edge at quayBottomY.
interface Foot {
  readonly at: Vec2;
  readonly y: number;
}

interface Joint {
  readonly before: number;
  readonly after: number;
  readonly end: Foot;
  readonly start: Foot;
  readonly fan: readonly [Foot, Foot] | null;
}

function shoreOf(edge: WaterEdge, index: number, heightAt: (p: Vec2) => number): Shore {
  const { a, b } = edge.segment;
  const [dx, dy] = [b[0] - a[0], b[1] - a[1]];
  const waterOnRight = dy * edge.towardWater[0] - dx * edge.towardWater[1] > 0;
  return waterOnRight ? { edge, index, heightAt, a, b } : { edge, index, heightAt, a: b, b: a };
}

function isBank(shore: Shore): boolean {
  return shore.edge.quay === 'earth';
}

function ownFoot(shore: Shore, at: Vec2): Foot {
  if (!isBank(shore)) return { at, y: WORLD.quayBottomY };
  const [nx, ny] = shore.edge.towardWater;
  return {
    at: [at[0] + nx * EARTH_SLOPE_DISTANCE, at[1] + ny * EARTH_SLOPE_DISTANCE],
    y: EARTH_BOTTOM_Y,
  };
}

function lengthOf({ a, b }: Shore): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1]);
}

// The point 1.5 m out from both edges, where two banks' lower edges meet: round a corner of land
// it lies past both banks' own feet, into a corner of water short of them. Either way it moves
// each foot along its edge, by at most half the edge, so no face leans past its own water edge.
function miterFoot(before: Shore, after: Shore): Foot | null {
  const [n1, n2] = [before.edge.towardWater, after.edge.towardWater];
  const cos = n1[0] * n2[0] + n1[1] * n2[1];
  if (cos <= -1) return null;
  const d1: Vec2 = [before.b[0] - before.a[0], before.b[1] - before.a[1]];
  const d2: Vec2 = [after.b[0] - after.a[0], after.b[1] - after.a[1]];
  const roundLand = d1[0] * d2[1] - d1[1] * d2[0] > 0;
  const shift = EARTH_SLOPE_DISTANCE * Math.sqrt((1 - cos) / (1 + cos));
  const fits =
    shift <= Math.min(lengthOf(before), lengthOf(after)) / 2 &&
    (!roundLand || Math.sqrt(2 / (1 + cos)) <= MAX_MITER);
  if (!fits) return null;
  const scale = EARTH_SLOPE_DISTANCE / (1 + cos);
  const v = before.b;
  return {
    at: [v[0] + (n1[0] + n2[0]) * scale, v[1] + (n1[1] + n2[1]) * scale],
    y: EARTH_BOTTOM_Y,
  };
}

// PLAN §5: quays run edge to edge along the water. Where a wall meets a bank, the bank's foot
// comes in under the wall's end, so both close on the same vertical edge; where two banks meet,
// they share one foot or, round a sharp corner of land, a fan between their feet.
function jointOf(shores: readonly Shore[], before: number, after: number): Joint {
  const [first, second] = [shores[before]!, shores[after]!];
  const v = first.b;
  const joint = (end: Foot, start: Foot, fan: Joint['fan']): Joint => ({
    before,
    after,
    end,
    start,
    fan,
  });
  if (!isBank(first) && !isBank(second)) return joint(ownFoot(first, v), ownFoot(second, v), null);
  if (!isBank(first) || !isBank(second)) {
    const under: Foot = { at: v, y: WORLD.quayBottomY };
    return joint(under, under, null);
  }
  const miter = miterFoot(first, second);
  if (miter) return joint(miter, miter, null);
  const [end, start] = [ownFoot(first, v), ownFoot(second, v)];
  return joint(end, start, [end, start]);
}

function keyOf(p: Vec2): string {
  return `${p[0]},${p[1]}`;
}

// Each shore runs on into the shore that starts where it ends; at the bounds the water runs on
// into the far ring and the quays end.
function jointsOf(shores: readonly Shore[]): readonly Joint[] {
  const byStart = new Map(shores.map((shore, i) => [keyOf(shore.a), i] as const));
  return shores.flatMap((shore, i) => {
    const after = byStart.get(keyOf(shore.b));
    return after === undefined || after === i ? [] : [jointOf(shores, i, after)];
  });
}

function faceHeight(shore: Shore): number {
  return shore.heightAt(shore.a) - (isBank(shore) ? EARTH_BOTTOM_Y : WORLD.quayBottomY);
}

// aMeta.z carries the surface's pattern preset: a wooden revetment draws its piling past the instanced
// posts' 60 m (streetscape plan §6.5). aFacade.zw carry the face's length and height, the extent its
// pattern space runs over.
function styleOf(shore: Shore, palette: PaletteModel): VertexStyle {
  const { quay, towardWater } = shore.edge;
  return {
    color: palette[QUAY_PALETTE[quay]].median.linear,
    surface: QUAY_SURFACE[quay],
    edgeId: edgeIdFor(shore.index + 10000),
    seed: 0,
    strokeAngle: Math.atan2(towardWater[1], towardWater[0]),
    extent: [lengthOf(shore), faceHeight(shore)],
    pattern: quay === 'wood' ? PATTERN.quayPiling : PATTERN.none,
  };
}

/* Pattern space runs u along the bank, on from the banks before it in its chain, so posts and planks
   keep their rhythm round a canal, and v up from the face's foot. */
function patternSpace(shore: Shore, startU: number): (world: Vec3) => Vec2 {
  const length = lengthOf(shore);
  const [dx, dy] = [(shore.b[0] - shore.a[0]) / length, (shore.b[1] - shore.a[1]) / length];
  const foot = isBank(shore) ? EARTH_BOTTOM_Y : WORLD.quayBottomY;
  return (world) => {
    const [x, y] = worldToLayout(world);
    return [startU + (x - shore.a[0]) * dx + (y - shore.a[1]) * dy, world[1] - foot];
  };
}

function footPoint(foot: Foot): Vec3 {
  return layoutToWorld(foot.at, foot.y);
}

function upward(corners: readonly [Vec3, Vec3, Vec3]): readonly [Vec3, Vec3, Vec3] {
  const [p, q, r] = corners;
  const turn = (q[2] - p[2]) * (r[0] - p[0]) - (q[0] - p[0]) * (r[2] - p[2]);
  return turn > 0 ? corners : [p, r, q];
}

// Each quay is cut at the middle of its top edge into three faces, so every face hangs from its
// own water edge away from the corners: a step back from a corner of land can already be water.
function shoreFaces(
  [footA, footB, topB, topA]: readonly [Vec3, Vec3, Vec3, Vec3],
  topMiddle: Vec3
): readonly (readonly [Vec3, Vec3, Vec3])[] {
  return [
    [footA, footB, topMiddle],
    [footB, topB, topMiddle],
    [footA, topMiddle, topA],
  ];
}

function buildShore(
  shore: Shore,
  feet: readonly [Foot, Foot],
  palette: PaletteModel,
  chunks: ChunkSink,
  startU: number
): void {
  const middle: Vec2 = [(shore.a[0] + shore.b[0]) / 2, (shore.a[1] + shore.b[1]) / 2];
  const builder = chunks.builderAt('infrastructure', middle);
  const style = styleOf(shore, palette);
  const corners = [
    footPoint(feet[0]),
    footPoint(feet[1]),
    layoutToWorld(shore.b, shore.heightAt(shore.b)),
    layoutToWorld(shore.a, shore.heightAt(shore.a)),
  ] as const;
  const uvOf = patternSpace(shore, startU);
  for (const face of shoreFaces(corners, layoutToWorld(middle, shore.heightAt(middle)))) {
    builder.polygon(style, FRAME, face, uvOf);
  }
}

/** A culvert or water stair replaces its full width of bank. Supported masonry side wings
 * close the earth slope at either edge, instead of leaving it over the opening or treads. */
function buildShoreAtOpening(
  shore: Shore,
  feet: readonly [Foot, Foot],
  mouth: BankOpening,
  palette: PaletteModel,
  chunks: ChunkSink,
  startU: number
): void {
  const length = lengthOf(shore);
  const unit: Vec2 = [(shore.b[0] - shore.a[0]) / length, (shore.b[1] - shore.a[1]) / length];
  const at = (s: number): Vec2 => [shore.a[0] + unit[0] * s, shore.a[1] + unit[1] * s];
  const footAt = (s: number): Foot => ({
    at: [
      feet[0].at[0] + ((feet[1].at[0] - feet[0].at[0]) * s) / length,
      feet[0].at[1] + ((feet[1].at[1] - feet[0].at[1]) * s) / length,
    ],
    y: feet[0].y + ((feet[1].y - feet[0].y) * s) / length,
  });
  const cutFoot = (s: number): Foot => {
    const foot = footAt(s),
      top = at(s),
      across = mouth.across;
    if (!across) return foot;
    const sideShift = (foot.at[0] - top[0]) * across[0] + (foot.at[1] - top[1]) * across[1];
    return footAt(s - sideShift / (unit[0] * across[0] + unit[1] * across[1]));
  };
  const centre =
    (mouth.centre[0] - shore.a[0]) * unit[0] + (mouth.centre[1] - shore.a[1]) * unit[1];
  const from = Math.max(0, centre - mouth.halfWidth);
  const to = Math.min(length, centre + mouth.halfWidth);
  for (const [a, b] of [
    [0, from],
    [to, length],
  ] as const) {
    if (b - a < 1e-5) continue;
    buildShore(
      { ...shore, a: at(a), b: at(b) },
      [a === to ? cutFoot(a) : footAt(a), b === from ? cutFoot(b) : footAt(b)],
      palette,
      chunks,
      startU + a
    );
  }
  // An earth slope meets the culvert's vertical masonry wings, rather than leaving a slit
  // beside the arch. Wooden cap revetments have no slope to close.
  if (isBank(shore)) {
    const builder = chunks.builderAt('infrastructure', mouth.centre);
    const style: VertexStyle = {
      ...styleOf(shore, palette),
      surface: SURFACE.stone,
      color: palette.wallBrick.median.linear,
      pattern: PATTERN.none,
    };
    for (const [s, sign] of [
      [from, 1],
      [to, -1],
    ] as const) {
      const p = at(s),
        foot = cutFoot(s);
      const points: Vec3[] = [
        layoutToWorld(p, shore.heightAt(p)),
        footPoint(foot),
        layoutToWorld(p, foot.y),
      ];
      orientedFace(builder, style, FRAME, points, [sign * unit[0], 0, -sign * unit[1]]);
    }
  }
}

export function buildQuays(
  waterEdges: readonly WaterEdge[],
  palette: PaletteModel,
  chunks: ChunkSink,
  flights: readonly WaterAccess[] = [],
  heightAt: (p: Vec2) => number = () => WORLD.groundY
): { readonly count: number } {
  const shores = waterEdges.map((edge, index) => shoreOf(edge, index, heightAt));
  const startU = banksOf(waterEdges).map((bank) => bank.startU);
  const joints = jointsOf(shores);
  const ends = new Map(joints.map((joint) => [joint.before, joint.end] as const));
  const starts = new Map(joints.map((joint) => [joint.after, joint.start] as const));
  const duiker = kattengatDuikerAt(waterEdges);
  const culvertMouths = new Map(
    duiker ? [duiker.inner, duiker.outer].map((mouth) => [mouth.edgeIndex, mouth] as const) : []
  );

  for (const shore of shores) {
    const footA = starts.get(shore.index) ?? ownFoot(shore, shore.a);
    const footB = ends.get(shore.index) ?? ownFoot(shore, shore.b);
    const mouth = culvertMouths.get(shore.index) ?? stairBankOpening(shore.edge, flights);
    if (mouth)
      buildShoreAtOpening(shore, [footA, footB], mouth, palette, chunks, startU[shore.index]!);
    else buildShore(shore, [footA, footB], palette, chunks, startU[shore.index]!);
  }

  for (const { before, fan } of joints) {
    if (!fan) continue;
    const shore = shores[before]!;
    const corner = layoutToWorld(shore.b, shore.heightAt(shore.b));
    const face = upward([corner, footPoint(fan[0]), footPoint(fan[1])]);
    const uvOf = patternSpace(shore, startU[shore.index]!);
    chunks.builderAt('infrastructure', shore.b).polygon(styleOf(shore, palette), FRAME, face, uvOf);
  }

  if (duiker) {
    const style: VertexStyle = {
      color: palette.wallBrick.median.linear,
      surface: SURFACE.stone,
      // Its inner arch replaces this source cap edge completely. Keep that edge's
      // identity instead of inventing an ID that can alias an unrelated bank.
      edgeId: edgeIdFor(duiker.inner.edgeIndex + 10000),
      seed: 0.51,
      strokeAngle: 0,
      extent: [3, 2.5],
    };
    const dark: VertexStyle = { ...style, color: palette.lineworkDark.median.linear };
    buildKattengatDuiker(
      chunks.builderAt('infrastructure', duiker.inner.centre),
      duiker,
      style,
      dark
    );
  }

  return { count: shores.length };
}
